import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
    ArrowLeft,
    CalendarClock,
    MessageCircle,
    Phone,
    Check,
    X,
    FileText,
    Loader2,
    UserRound,
    Pill,
    Trash2,
    Plus,
    Bookmark,
    ChevronLeft,
    ChevronRight,
} from "lucide-react";
import { api } from "@/lib/api";
import { Money } from "@/components/ui-veda/Primitives";
import { TID } from "@/constants/testIds";

const TABS = [
    { key: "upcoming", label: "Upcoming" },
    { key: "past", label: "Past" },
    { key: "calendar", label: "Calendar" },
    { key: "all", label: "All" },
];

function parseSlotToDate(slot) {
    // slot format: "YYYY-MM-DD HH:MM"
    if (!slot || typeof slot !== "string") return null;
    const m = slot.match(/^(\d{4}-\d{2}-\d{2})\s+(\d{1,2}):(\d{2})$/);
    if (!m) return null;
    const [, ymd, hh, mm] = m;
    return new Date(`${ymd}T${hh.padStart(2, "0")}:${mm}:00`);
}

function formatSlot(slot) {
    const d = parseSlotToDate(slot);
    if (!d || isNaN(d.getTime())) return slot || "—";
    return d.toLocaleString(undefined, {
        weekday: "short",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
    });
}

function StatusPill({ status }) {
    const map = {
        confirmed: {
            label: "Confirmed",
            cls: "bg-veda-brand/10 text-veda-brand border-veda-brand/40",
        },
        completed: {
            label: "Completed",
            cls: "bg-veda-success/10 text-veda-success border-veda-success/40",
        },
        cancelled: {
            label: "Cancelled",
            cls: "bg-veda-error/10 text-veda-error border-veda-error/40",
        },
    };
    const conf = map[status] || map.confirmed;
    return (
        <span
            className={`rounded-pill border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] ${conf.cls}`}
        >
            {conf.label}
        </span>
    );
}

export default function DoctorAppointments() {
    const [items, setItems] = useState([]);
    const [tab, setTab] = useState("upcoming");
    const [loading, setLoading] = useState(true);
    const [busyId, setBusyId] = useState(null);
    const [rxCid, setRxCid] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/consultations");
            setItems(data);
        } catch {
            toast.error("Could not load appointments.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const filtered = useMemo(() => {
        const now = Date.now();
        return items
            .map((c) => ({
                ...c,
                _when: parseSlotToDate(c.slot)?.getTime() ?? 0,
            }))
            .filter((c) => {
                if (tab === "all") return true;
                const upcoming =
                    c.status !== "cancelled" &&
                    c.status !== "completed" &&
                    c._when >= now - 60 * 60 * 1000; // 1h grace
                if (tab === "upcoming") return upcoming;
                if (tab === "past") return !upcoming;
                return true;
            })
            .sort((a, b) =>
                tab === "past" ? b._when - a._when : a._when - b._when,
            );
    }, [items, tab]);

    async function setStatus(cid, status) {
        setBusyId(cid);
        try {
            await api.patch(`/consultations/${cid}/status`, { status });
            toast.success(
                status === "completed"
                    ? "Marked as completed."
                    : status === "cancelled"
                      ? "Appointment cancelled."
                      : "Status updated.",
            );
            setItems((prev) =>
                prev.map((c) =>
                    c.consultation_id === cid ? { ...c, status } : c,
                ),
            );
        } catch (e) {
            const detail = e?.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Could not update.");
        } finally {
            setBusyId(null);
        }
    }

    async function removePrescription(cid, rxId) {
        if (!window.confirm("Remove this prescription?")) return;
        try {
            await api.delete(`/consultations/${cid}/prescriptions/${rxId}`);
            toast.success("Removed.");
            setItems((prev) =>
                prev.map((c) =>
                    c.consultation_id === cid
                        ? {
                              ...c,
                              prescriptions: (c.prescriptions || []).filter(
                                  (r) => r.prescription_id !== rxId,
                              ),
                          }
                        : c,
                ),
            );
        } catch {
            toast.error("Could not remove prescription.");
        }
    }

    function handleRxSaved(cid, rx) {
        setItems((prev) =>
            prev.map((c) =>
                c.consultation_id === cid
                    ? { ...c, prescriptions: [...(c.prescriptions || []), rx] }
                    : c,
            ),
        );
        setRxCid(null);
    }

    const counts = useMemo(() => {
        const now = Date.now();
        const up = items.filter(
            (c) =>
                c.status !== "cancelled" &&
                c.status !== "completed" &&
                (parseSlotToDate(c.slot)?.getTime() ?? 0) >= now - 60 * 60 * 1000,
        ).length;
        return { upcoming: up, all: items.length };
    }, [items]);

    return (
        <div data-testid={TID.doctorAppointments} className="pb-16">
            <div className="border-b border-veda-border">
                <div className="veda-container py-8 md:py-10">
                    <Link
                        to="/doctor/dashboard"
                        className="inline-flex items-center gap-1 text-xs font-medium text-veda-muted hover:text-veda-onSurface"
                    >
                        <ArrowLeft size={14} /> Dashboard
                    </Link>
                    <div className="mt-3 flex flex-wrap items-baseline justify-between gap-4">
                        <div>
                            <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-veda-brand">
                                Bookings
                            </p>
                            <h1 className="font-display mt-1 text-4xl text-veda-onSurface md:text-5xl">
                                Appointments
                            </h1>
                        </div>
                        <div className="text-right">
                            <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-veda-muted">
                                Upcoming
                            </p>
                            <p
                                data-testid={TID.doctorAppointmentsUpcomingCount}
                                className="font-display text-3xl text-veda-onSurface"
                            >
                                {counts.upcoming}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <div className="sticky top-16 z-10 border-b border-veda-border bg-veda-surface">
                <div className="veda-container no-scrollbar flex gap-2 overflow-x-auto py-3">
                    {TABS.map((t) => (
                        <button
                            key={t.key}
                            onClick={() => setTab(t.key)}
                            data-testid={TID.doctorAppointmentsTab(t.key)}
                            className={`inline-flex items-center gap-2 border px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.14em] ${
                                tab === t.key
                                    ? "border-veda-inverse bg-veda-inverse text-veda-onInverse"
                                    : "border-veda-border bg-veda-surface text-veda-onSurface"
                            }`}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>
            </div>

            <div className="veda-container pt-6 md:pt-8">
                {tab === "calendar" ? (
                    <CalendarGrid items={items} />
                ) : loading ? (
                    <div className="border border-veda-border bg-veda-surface2 p-10 text-center text-sm text-veda-muted">
                        Loading appointments…
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="border border-veda-border bg-veda-surface2 p-10 text-center text-sm text-veda-muted">
                        {tab === "upcoming"
                            ? "No upcoming appointments. New bookings will appear here automatically."
                            : "Nothing here yet."}
                    </div>
                ) : (
                    <ul className="grid gap-4">
                        {filtered.map((c) => (
                            <li
                                key={c.consultation_id}
                                data-testid={TID.doctorAppointmentCard(c.consultation_id)}
                                className="border border-veda-border bg-veda-surface p-5"
                            >
                                <div className="flex flex-wrap items-start justify-between gap-3">
                                    <div className="flex items-start gap-3">
                                        <Link
                                            to={`/doctor/patient/${c.customer_id}`}
                                            data-testid={TID.doctorAppointmentPatientAvatar(c.consultation_id)}
                                            aria-label="Open patient profile"
                                            className="grid h-11 w-11 place-items-center bg-veda-brand/10 text-veda-brand transition-colors hover:bg-veda-brand/20"
                                        >
                                            <UserRound size={18} />
                                        </Link>
                                        <div>
                                            <Link
                                                to={`/doctor/patient/${c.customer_id}`}
                                                data-testid={TID.doctorAppointmentPatientName(c.consultation_id)}
                                                className="font-display text-lg text-veda-onSurface hover:text-veda-brand"
                                            >
                                                {c.customer_name}
                                            </Link>
                                            <p className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-veda-muted">
                                                <span className="inline-flex items-center gap-1">
                                                    <CalendarClock size={12} />
                                                    {formatSlot(c.slot)}
                                                </span>
                                                <span>·</span>
                                                <span className="inline-flex items-center gap-1">
                                                    {c.mode === "call" ? (
                                                        <Phone size={12} />
                                                    ) : (
                                                        <MessageCircle size={12} />
                                                    )}
                                                    {c.mode === "call" ? "Voice call" : "Chat"}
                                                </span>
                                                <span>·</span>
                                                <span className="font-semibold text-veda-onSurface">
                                                    <Money value={c.fee} />
                                                </span>
                                            </p>
                                        </div>
                                    </div>
                                    <StatusPill status={c.status || "confirmed"} />
                                </div>

                                {c.notes && (
                                    <div className="mt-4 flex items-start gap-2 border border-veda-divider bg-veda-surface2/50 p-3 text-xs text-veda-onSurface/80">
                                        <FileText
                                            size={13}
                                            className="mt-0.5 shrink-0 text-veda-muted"
                                        />
                                        <p className="leading-relaxed">{c.notes}</p>
                                    </div>
                                )}

                                {(c.prescriptions || []).length > 0 && (
                                    <div
                                        data-testid={TID.doctorAppointmentPrescriptions(c.consultation_id)}
                                        className="mt-4 border border-veda-brand/30 bg-veda-brand/[0.05] p-3"
                                    >
                                        <p className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-veda-brand">
                                            <Pill size={11} /> Prescriptions ({c.prescriptions.length})
                                        </p>
                                        <ul className="mt-2 flex flex-col gap-1.5">
                                            {c.prescriptions.map((r) => (
                                                <li
                                                    key={r.prescription_id}
                                                    className="flex items-start justify-between gap-3 text-[11px] text-veda-onSurface"
                                                >
                                                    <span className="min-w-0 flex-1">
                                                        <span className="font-semibold">
                                                            {r.medicine}
                                                        </span>
                                                        {" · "}
                                                        {r.dosage}
                                                        {" · "}
                                                        {r.frequency}
                                                        {" · "}
                                                        <span className="font-semibold">
                                                            {r.days}d
                                                        </span>
                                                        {r.notes && (
                                                            <span className="mt-0.5 block italic text-veda-muted">
                                                                {r.notes}
                                                            </span>
                                                        )}
                                                    </span>
                                                    <button
                                                        onClick={() =>
                                                            removePrescription(
                                                                c.consultation_id,
                                                                r.prescription_id,
                                                            )
                                                        }
                                                        data-testid={TID.doctorAppointmentRxRemove(r.prescription_id)}
                                                        className="p-1 text-veda-error"
                                                        aria-label="Remove prescription"
                                                    >
                                                        <Trash2 size={12} />
                                                    </button>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                <div className="mt-4 flex flex-wrap items-center gap-2">
                                    <Link
                                        to={`/consultation/${c.consultation_id}`}
                                        data-testid={TID.doctorAppointmentOpenChat(c.consultation_id)}
                                        className="inline-flex items-center gap-1.5 bg-veda-inverse px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-veda-onInverse"
                                    >
                                        <MessageCircle size={11} /> Open chat
                                    </Link>
                                    <button
                                        onClick={() => setRxCid(c.consultation_id)}
                                        data-testid={TID.doctorAppointmentAddRx(c.consultation_id)}
                                        className="inline-flex items-center gap-1.5 border border-veda-brand bg-veda-brand/10 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-veda-brand"
                                    >
                                        <Plus size={11} /> Prescription
                                    </button>
                                    {c.status !== "completed" && c.status !== "cancelled" && (
                                        <>
                                            <button
                                                onClick={() =>
                                                    setStatus(c.consultation_id, "completed")
                                                }
                                                disabled={busyId === c.consultation_id}
                                                data-testid={TID.doctorAppointmentComplete(c.consultation_id)}
                                                className="inline-flex items-center gap-1.5 border border-veda-success bg-veda-success px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-white disabled:opacity-70"
                                            >
                                                {busyId === c.consultation_id ? (
                                                    <Loader2 size={11} className="animate-spin" />
                                                ) : (
                                                    <Check size={11} />
                                                )}
                                                Mark completed
                                            </button>
                                            <button
                                                onClick={() =>
                                                    setStatus(c.consultation_id, "cancelled")
                                                }
                                                disabled={busyId === c.consultation_id}
                                                data-testid={TID.doctorAppointmentCancel(c.consultation_id)}
                                                className="inline-flex items-center gap-1.5 border border-veda-error px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-veda-error disabled:opacity-70"
                                            >
                                                <X size={11} /> Cancel
                                            </button>
                                        </>
                                    )}
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            {rxCid && (
                <PrescriptionModal
                    cid={rxCid}
                    onClose={() => setRxCid(null)}
                    onSaved={(rx) => handleRxSaved(rxCid, rx)}
                />
            )}
        </div>
    );
}

function CalendarGrid({ items }) {
    const [cursor, setCursor] = useState(() => {
        const d = new Date();
        return { y: d.getFullYear(), m: d.getMonth() }; // m: 0-11
    });

    const monthLabel = new Date(cursor.y, cursor.m, 1).toLocaleDateString(
        undefined,
        { month: "long", year: "numeric" },
    );

    const grid = useMemo(() => {
        const first = new Date(cursor.y, cursor.m, 1);
        const daysInMonth = new Date(cursor.y, cursor.m + 1, 0).getDate();
        const startDow = first.getDay(); // 0=Sun
        // Group appointments by YYYY-MM-DD
        const byDate = {};
        for (const c of items) {
            const m = (c.slot || "").match(/^(\d{4}-\d{2}-\d{2})/);
            if (!m) continue;
            const key = m[1];
            (byDate[key] ||= []).push(c);
        }
        const cells = [];
        for (let i = 0; i < startDow; i++) cells.push(null);
        for (let d = 1; d <= daysInMonth; d++) {
            const dateStr = `${cursor.y}-${String(cursor.m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
            cells.push({ day: d, dateStr, appts: byDate[dateStr] || [] });
        }
        return cells;
    }, [cursor, items]);

    const todayStr = new Date().toISOString().slice(0, 10);

    function prev() {
        setCursor((c) =>
            c.m === 0 ? { y: c.y - 1, m: 11 } : { y: c.y, m: c.m - 1 },
        );
    }
    function next() {
        setCursor((c) =>
            c.m === 11 ? { y: c.y + 1, m: 0 } : { y: c.y, m: c.m + 1 },
        );
    }
    function today() {
        const d = new Date();
        setCursor({ y: d.getFullYear(), m: d.getMonth() });
    }

    return (
        <div data-testid={TID.doctorCalendar}>
            <div className="flex items-center justify-between border border-veda-border bg-veda-surface px-4 py-3">
                <div className="flex items-center gap-2">
                    <button
                        onClick={prev}
                        data-testid={TID.doctorCalendarPrev}
                        className="p-1.5 text-veda-onSurface hover:bg-veda-surface2"
                        aria-label="Previous month"
                    >
                        <ChevronLeft size={14} />
                    </button>
                    <p className="font-display text-lg text-veda-onSurface">
                        {monthLabel}
                    </p>
                    <button
                        onClick={next}
                        data-testid={TID.doctorCalendarNext}
                        className="p-1.5 text-veda-onSurface hover:bg-veda-surface2"
                        aria-label="Next month"
                    >
                        <ChevronRight size={14} />
                    </button>
                </div>
                <button
                    onClick={today}
                    className="border border-veda-border bg-veda-surface px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-veda-onSurface"
                >
                    Today
                </button>
            </div>

            <div className="mt-2 grid grid-cols-7 gap-px bg-veda-border">
                {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
                    <div
                        key={d}
                        className="bg-veda-surface2 px-2 py-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-veda-muted"
                    >
                        {d}
                    </div>
                ))}
                {grid.map((cell, idx) => {
                    if (!cell) {
                        return (
                            <div
                                key={`e-${idx}`}
                                className="min-h-[80px] bg-veda-surface2/40 md:min-h-[120px]"
                            />
                        );
                    }
                    const isToday = cell.dateStr === todayStr;
                    return (
                        <div
                            key={cell.dateStr}
                            data-testid={TID.doctorCalendarDay(cell.dateStr)}
                            className={`min-h-[80px] bg-veda-surface p-1.5 md:min-h-[120px] ${
                                isToday ? "ring-2 ring-veda-brand" : ""
                            }`}
                        >
                            <p
                                className={`text-[10px] font-semibold ${
                                    isToday
                                        ? "text-veda-brand"
                                        : "text-veda-muted"
                                }`}
                            >
                                {cell.day}
                            </p>
                            <ul className="mt-1 flex flex-col gap-0.5">
                                {cell.appts.slice(0, 4).map((c) => {
                                    const time = (c.slot || "").split(" ")[1] || "";
                                    const badge =
                                        c.status === "completed"
                                            ? "bg-veda-success/15 text-veda-success"
                                            : c.status === "cancelled"
                                              ? "bg-veda-error/15 text-veda-error line-through"
                                              : "bg-veda-brand/15 text-veda-brand";
                                    return (
                                        <Link
                                            key={c.consultation_id}
                                            to={`/consultation/${c.consultation_id}`}
                                            title={`${c.customer_name} · ${c.slot} · ${c.mode}`}
                                            className={`inline-flex items-center gap-1 truncate px-1 py-0.5 text-[9px] font-semibold ${badge}`}
                                        >
                                            {time && (
                                                <span className="tabular-nums">
                                                    {time}
                                                </span>
                                            )}
                                            <span className="truncate">
                                                {c.customer_name}
                                            </span>
                                        </Link>
                                    );
                                })}
                                {cell.appts.length > 4 && (
                                    <p className="text-[9px] text-veda-muted">
                                        +{cell.appts.length - 4} more
                                    </p>
                                )}
                            </ul>
                        </div>
                    );
                })}
            </div>
            <p className="mt-3 text-[10px] text-veda-muted">
                Green = confirmed · dark green = completed · red = cancelled.
                Click any pill to open the consultation.
            </p>
        </div>
    );
}

function PrescriptionModal({ cid, onClose, onSaved }) {
    const [medicine, setMedicine] = useState("");
    const [dosage, setDosage] = useState("");
    const [frequency, setFrequency] = useState("");
    const [days, setDays] = useState(14);
    const [notes, setNotes] = useState("");
    const [saving, setSaving] = useState(false);
    const [templates, setTemplates] = useState([]);
    const [templateName, setTemplateName] = useState("");
    const [savingTpl, setSavingTpl] = useState(false);

    useEffect(() => {
        api
            .get("/doctor/prescription-templates")
            .then((r) => setTemplates(r.data))
            .catch(() => {});
    }, []);

    function applyTemplate(tid) {
        if (!tid) return;
        const t = templates.find((x) => x.template_id === tid);
        if (!t) return;
        setMedicine(t.medicine || "");
        setDosage(t.dosage || "");
        setFrequency(t.frequency || "");
        setDays(t.days || 14);
        setNotes(t.notes || "");
        toast.success(`Applied "${t.name}"`);
    }

    async function saveAsTemplate() {
        const name = (templateName || medicine).trim();
        if (!name) return toast.error("Give this template a name first.");
        if (!medicine.trim() || !dosage.trim() || !frequency.trim())
            return toast.error("Fill the prescription fields before saving as template.");
        setSavingTpl(true);
        try {
            const { data } = await api.post("/doctor/prescription-templates", {
                name,
                medicine: medicine.trim(),
                dosage: dosage.trim(),
                frequency: frequency.trim(),
                days: Number(days),
                notes: notes.trim() || undefined,
            });
            setTemplates((prev) => [data, ...prev]);
            setTemplateName("");
            toast.success("Saved as template.");
        } catch {
            toast.error("Could not save template.");
        } finally {
            setSavingTpl(false);
        }
    }

    async function removeTemplate(tid, e) {
        e.stopPropagation();
        if (!window.confirm("Delete this template?")) return;
        try {
            await api.delete(`/doctor/prescription-templates/${tid}`);
            setTemplates((prev) => prev.filter((t) => t.template_id !== tid));
        } catch {
            toast.error("Could not delete.");
        }
    }

    async function save(e) {
        e.preventDefault();
        if (!medicine.trim()) return toast.error("Medicine name is required.");
        if (!dosage.trim()) return toast.error("Dosage is required.");
        if (!frequency.trim()) return toast.error("Frequency is required.");
        setSaving(true);
        try {
            const { data } = await api.post(
                `/consultations/${cid}/prescriptions`,
                {
                    medicine: medicine.trim(),
                    dosage: dosage.trim(),
                    frequency: frequency.trim(),
                    days: Number(days),
                    notes: notes.trim() || undefined,
                },
            );
            toast.success("Prescription added.");
            onSaved(data);
        } catch (e2) {
            const detail = e2?.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Could not save.");
        } finally {
            setSaving(false);
        }
    }

    return (
        <div
            data-testid={TID.doctorRxModal}
            onClick={onClose}
            className="fixed inset-0 z-40 flex items-end bg-veda-inverse/40 backdrop-blur-sm md:items-center md:justify-center"
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className="w-full max-w-lg border border-veda-border bg-veda-surface p-5"
            >
                <div className="flex items-start justify-between">
                    <div>
                        <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-veda-brand">
                            Prescription
                        </p>
                        <h3 className="font-display mt-1 text-xl text-veda-onSurface">
                            Write a remedy plan
                        </h3>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-1.5 text-veda-muted"
                        aria-label="Close"
                    >
                        <X size={16} />
                    </button>
                </div>
                <form onSubmit={save} className="mt-5 flex flex-col gap-3">
                    {templates.length > 0 && (
                        <div className="border border-veda-brand/25 bg-veda-brand/[0.05] p-3">
                            <div className="flex items-center justify-between gap-2">
                                <p className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-veda-brand">
                                    <Bookmark size={11} /> Use a template
                                </p>
                            </div>
                            <div className="mt-2 flex flex-wrap gap-1.5">
                                {templates.map((t) => (
                                    <div
                                        key={t.template_id}
                                        className="group inline-flex items-center gap-1 rounded-pill border border-veda-brand/40 bg-veda-surface"
                                    >
                                        <button
                                            type="button"
                                            onClick={() => applyTemplate(t.template_id)}
                                            data-testid={TID.doctorRxTemplateApply(t.template_id)}
                                            className="px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-veda-onSurface"
                                        >
                                            {t.name}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={(e) => removeTemplate(t.template_id, e)}
                                            className="pr-1.5 text-veda-error opacity-0 group-hover:opacity-100"
                                            aria-label="Delete template"
                                        >
                                            <X size={10} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                    <input
                        placeholder="Medicine (e.g. Ashwagandha churna)"
                        value={medicine}
                        onChange={(e) => setMedicine(e.target.value)}
                        data-testid={TID.doctorRxMedicine}
                        required
                        className="border border-veda-border bg-veda-surface px-3 py-2.5 text-sm"
                    />
                    <div className="grid grid-cols-2 gap-3">
                        <input
                            placeholder="Dosage (e.g. 3g)"
                            value={dosage}
                            onChange={(e) => setDosage(e.target.value)}
                            data-testid={TID.doctorRxDosage}
                            required
                            className="border border-veda-border bg-veda-surface px-3 py-2.5 text-sm"
                        />
                        <input
                            type="number"
                            min={1}
                            max={365}
                            placeholder="Days"
                            value={days}
                            onChange={(e) => setDays(e.target.value)}
                            data-testid={TID.doctorRxDays}
                            required
                            className="border border-veda-border bg-veda-surface px-3 py-2.5 text-sm"
                        />
                    </div>
                    <input
                        placeholder="Frequency (e.g. twice daily after meals with warm milk)"
                        value={frequency}
                        onChange={(e) => setFrequency(e.target.value)}
                        data-testid={TID.doctorRxFrequency}
                        required
                        className="border border-veda-border bg-veda-surface px-3 py-2.5 text-sm"
                    />
                    <textarea
                        placeholder="Additional notes (optional) — e.g. avoid cold drinks; follow-up in 2 weeks"
                        rows={3}
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        data-testid={TID.doctorRxNotes}
                        className="border border-veda-border bg-veda-surface px-3 py-2.5 text-sm"
                    />
                    <div className="flex flex-wrap items-center gap-2 border-t border-veda-divider pt-3">
                        <input
                            placeholder="Template name (e.g. Vata insomnia set)"
                            value={templateName}
                            onChange={(e) => setTemplateName(e.target.value)}
                            data-testid={TID.doctorRxTemplateName}
                            className="flex-1 border border-veda-border bg-veda-surface px-2.5 py-1.5 text-xs"
                        />
                        <button
                            type="button"
                            onClick={saveAsTemplate}
                            disabled={savingTpl}
                            data-testid={TID.doctorRxTemplateSave}
                            className="inline-flex items-center gap-1 border border-veda-border bg-veda-surface px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-veda-onSurface disabled:opacity-60"
                        >
                            <Bookmark size={11} />
                            Save as template
                        </button>
                    </div>
                    <div className="flex items-center justify-end gap-2">
                        <button
                            type="button"
                            onClick={onClose}
                            className="border border-veda-border bg-veda-surface px-4 py-2 text-xs font-semibold uppercase tracking-[0.14em] text-veda-onSurface"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={saving}
                            data-testid={TID.doctorRxSubmit}
                            className="inline-flex items-center gap-2 bg-veda-brand px-4 py-2 text-xs font-semibold uppercase tracking-[0.14em] text-white disabled:opacity-70"
                        >
                            {saving && <Loader2 size={12} className="animate-spin" />}
                            Save prescription
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
